import DecimalType from "decimal.js";

import { Allergen, PricingPlan, SubscriptionItemType } from "@/primary-models";

export enum FreshExtraProduct {
  SPRINKLES = "SPRINKLES",
  NIBBLES_CHICKEN = "NIBBLES_CHICKEN",
  NIBBLES_SALMON = "NIBBLES_SALMON",
  NIBBLES_CHICKEN_POUCH = "NIBBLES_CHICKEN_POUCH",
  NIBBLES_SALMON_POUCH = "NIBBLES_SALMON_POUCH",
  NIBBLES_TUNA_POUCH = "NIBBLES_TUNA_POUCH",
}

export type TFreshExtraProduct = keyof typeof FreshExtraProduct;

export type TNibblesProduct =
  | "NIBBLES_CHICKEN"
  | "NIBBLES_SALMON"
  | "NIBBLES_CHICKEN_POUCH"
  | "NIBBLES_SALMON_POUCH"
  | "NIBBLES_TUNA_POUCH";

export type TNibblesPouchProduct = Extract<TNibblesProduct,
  | "NIBBLES_CHICKEN_POUCH"
  | "NIBBLES_SALMON_POUCH"
  | "NIBBLES_TUNA_POUCH"
>;

export type TSprinklesProduct = Extract<TFreshExtraProduct, "SPRINKLES">;

export type FreshExtraItemQuantity = Partial<Record<TFreshExtraProduct, number>>;
export type FreshExtraItemPostChargeQuantity = Partial<Record<TFreshExtraProduct, number | null>>;

export interface IUpdateExtraItemRequest {
  subscriptionId: number;
  items: FreshExtraItemQuantity;
}

export interface IUpdateExtraItemResponse {
  subscriptionId: number;
  items: FreshExtraItemQuantity;
}

/**
 * Allowed free treat extras, in recommendation preference order.
 * GET /customer/subscription/fresh/free-treat-gift recommends the first entry the cat is not allergic to.
 * POST /customer/subscription/fresh/free-treat-gift-and-delay uses this as an allowlist only
 * (order does not matter there).
 */
export const ALLOWED_FREE_TREAT_GIFT_PRODUCTS = [
  FreshExtraProduct.SPRINKLES,
  FreshExtraProduct.NIBBLES_TUNA_POUCH,
] as const;
export type TAllowedFreeTreatGiftProduct = typeof ALLOWED_FREE_TREAT_GIFT_PRODUCTS[number];

export const FREE_TREAT_GIFT_COMMENTS =
  "This request was generated by the system and submitted by the customer via the website cancellation flow.";

/** A Fresh subscription may receive at most one free treat gift in this many days. */
export const FREE_TREAT_GIFT_COOLDOWN_DAYS = 30;

/** When accepting the free treat save offer, push the next delivery back by this many weeks. */
export const FREE_TREAT_GIFT_DELAY_WEEKS = 2;

export interface IRequestFreeTreatGiftAndDelayRequest {
  subscriptionId: number;
  product: TAllowedFreeTreatGiftProduct;
  reason: string;
}

export interface IRequestFreeTreatGiftAndDelayResponse {
  id: number;
  subscriptionId: number;
  /** ISO date (YYYY-MM-DD) of the subscription's next delivery after the 2-week delay. */
  newDeliveryDate: string;
}

export interface IGetFreeTreatGiftQuery {
  subscriptionId: number;
}

export interface IGetFreeTreatGiftResponse {
  subscriptionId: number;
  /** True if this Fresh subscription already has a gift in the last 30 days. */
  alreadyRedeemed: boolean;
  /**
   * SPRINKLES unless the cat is allergic to that extra,
   * then NIBBLES_TUNA_POUCH. Null if the cat cannot have either.
   */
  recommendedProduct: TAllowedFreeTreatGiftProduct | null;
}

export interface INibblesUpsellRequest {
  product: TNibblesProduct;
}

export interface INibblesUpsellResponse {
  /** The subscription id to which the product was added */
  subscriptionId: number;
}

// Each extras item occupies a certain number of centiJars (base units)of space in the box
export const CENTIJAR_UNITS_PER_ITEM: Record<TFreshExtraProduct, number> = {
  [FreshExtraProduct.SPRINKLES]: 100,
  [FreshExtraProduct.NIBBLES_CHICKEN_POUCH]: 150,
  [FreshExtraProduct.NIBBLES_SALMON_POUCH]: 150,
  [FreshExtraProduct.NIBBLES_TUNA_POUCH]: 150,
  [FreshExtraProduct.NIBBLES_CHICKEN]: 300,
  [FreshExtraProduct.NIBBLES_SALMON]: 300,
} as const;

// Total capacity of standard box (fresh-box-2 or fresh-box-3) = 12 tubes or 3600 centiJars
// Total capacity of trial/freezer box (fresh-box-1) = 6 tubes or 1800 centiJars
type ALLOWED_CENTIJAR_UNITS_IN_BOX = 1800 | 3600;

export const MAX_FRESH_EXTRAS_CAPACITY_PER_BOX: Record<PricingPlan, ALLOWED_CENTIJAR_UNITS_IN_BOX> = {
  [PricingPlan.TRIAL]: 1800,
  [PricingPlan.TRIAL_WEEK]: 1800,
  [PricingPlan.FREEZER]: 1800,
  [PricingPlan.STANDARD]: 3600,
  [PricingPlan.HALF]: 3600,
  [PricingPlan.SAVER]: 3600,
} as const;

export const ALLERGEN_TO_EXTRAS_MAP: Record<Allergen, TFreshExtraProduct[]> = {
  [Allergen.CHICKEN]: [
    FreshExtraProduct.SPRINKLES,
    FreshExtraProduct.NIBBLES_CHICKEN,
    FreshExtraProduct.NIBBLES_CHICKEN_POUCH,
  ],
  [Allergen.TURKEY]: [],
  [Allergen.BEEF]: [],
  [Allergen.PORK]: [],
  [Allergen.LAMB]: [],
  [Allergen.WHITEFISH]: [],
  [Allergen.DUCK]: [],
  [Allergen.SALMON]: [
    FreshExtraProduct.NIBBLES_SALMON,
    FreshExtraProduct.NIBBLES_SALMON_POUCH,
  ],
  [Allergen.TUNA]: [
    FreshExtraProduct.NIBBLES_TUNA_POUCH,
  ],
};

// This is mostly to remind us to update the fresh extra product enum when we add a new recipe.
export const IS_FRESH_EXTRA_PRODUCT: Record<TFreshExtraProduct, true>
& Record<Exclude<SubscriptionItemType, TFreshExtraProduct>, false> = {
  [SubscriptionItemType.CLUCK]: false,
  [SubscriptionItemType.GOBBLE]: false,
  [SubscriptionItemType.MOOO]: false,
  [SubscriptionItemType.OINK]: false,
  [SubscriptionItemType.QUACK]: false,
  [SubscriptionItemType.SPLASH]: false,
  [SubscriptionItemType.BAAA]: false,
  [SubscriptionItemType.SWISH]: false,
  [SubscriptionItemType.SCOOP_PLANET]: false,
  [SubscriptionItemType.SCOOP_HEALTH]: false,
  [SubscriptionItemType.NIBBLES_SALMON]: true,
  [SubscriptionItemType.NIBBLES_CHICKEN]: true,
  [SubscriptionItemType.NIBBLES_SALMON_POUCH]: true,
  [SubscriptionItemType.NIBBLES_CHICKEN_POUCH]: true,
  [SubscriptionItemType.NIBBLES_TUNA_POUCH]: true,
  [SubscriptionItemType.SPRINKLES]: true,
  [SubscriptionItemType.TOTE_BAG]: false,
  [SubscriptionItemType.SHREDDED_CLUCK]: false,
  [SubscriptionItemType.SHREDDED_GOBBLE]: false,
};

export function isFreshExtraProduct(product: string): product is TFreshExtraProduct {
  return !!(IS_FRESH_EXTRA_PRODUCT as Partial<Record<string, boolean>>)[product];
}

export type TExtrasPriceInfo = Record<SubscriptionItemType, DecimalType>;

export const IS_NIBBLES_PRODUCT: Record<TNibblesProduct, true>
  & Record<Exclude<SubscriptionItemType, TNibblesProduct>, false> = {
    [SubscriptionItemType.CLUCK]: false,
    [SubscriptionItemType.GOBBLE]: false,
    [SubscriptionItemType.MOOO]: false,
    [SubscriptionItemType.OINK]: false,
    [SubscriptionItemType.QUACK]: false,
    [SubscriptionItemType.SPLASH]: false,
    [SubscriptionItemType.BAAA]: false,
    [SubscriptionItemType.SWISH]: false,
    [SubscriptionItemType.SCOOP_PLANET]: false,
    [SubscriptionItemType.SCOOP_HEALTH]: false,
    [SubscriptionItemType.NIBBLES_SALMON]: true,
    [SubscriptionItemType.NIBBLES_CHICKEN]: true,
    [SubscriptionItemType.NIBBLES_SALMON_POUCH]: true,
    [SubscriptionItemType.NIBBLES_CHICKEN_POUCH]: true,
    [SubscriptionItemType.NIBBLES_TUNA_POUCH]: true,
    [SubscriptionItemType.SPRINKLES]: false,
    [SubscriptionItemType.TOTE_BAG]: false,
    [SubscriptionItemType.SHREDDED_CLUCK]: false,
    [SubscriptionItemType.SHREDDED_GOBBLE]: false,
  };

export function isNibblesProduct(product: string): product is TNibblesProduct {
  return !!(IS_NIBBLES_PRODUCT as Partial<Record<string, boolean>>)[product];
}

export const NIBBLES_PRODUCTS = Object.values(SubscriptionItemType).filter(isNibblesProduct);

export const IS_NIBBLES_POUCH_PRODUCT: Record<TNibblesPouchProduct, true>
  & Record<Exclude<SubscriptionItemType, TNibblesPouchProduct>, false> = {
    [SubscriptionItemType.CLUCK]: false,
    [SubscriptionItemType.GOBBLE]: false,
    [SubscriptionItemType.MOOO]: false,
    [SubscriptionItemType.OINK]: false,
    [SubscriptionItemType.QUACK]: false,
    [SubscriptionItemType.SPLASH]: false,
    [SubscriptionItemType.BAAA]: false,
    [SubscriptionItemType.SWISH]: false,
    [SubscriptionItemType.SCOOP_PLANET]: false,
    [SubscriptionItemType.SCOOP_HEALTH]: false,
    [SubscriptionItemType.NIBBLES_SALMON]: false,
    [SubscriptionItemType.NIBBLES_CHICKEN]: false,
    [SubscriptionItemType.NIBBLES_SALMON_POUCH]: true,
    [SubscriptionItemType.NIBBLES_CHICKEN_POUCH]: true,
    [SubscriptionItemType.NIBBLES_TUNA_POUCH]: true,
    [SubscriptionItemType.SPRINKLES]: false,
    [SubscriptionItemType.TOTE_BAG]: false,
    [SubscriptionItemType.SHREDDED_CLUCK]: false,
    [SubscriptionItemType.SHREDDED_GOBBLE]: false,
  };

export function isNibblesPouchProduct(product: string): product is TNibblesPouchProduct {
  return !!(IS_NIBBLES_POUCH_PRODUCT as Partial<Record<string, boolean>>)[product];
}

export const NIBBLES_POUCH_PRODUCTS = Object.values(SubscriptionItemType).filter(isNibblesPouchProduct);

export const NIBBLES_POUCH_MULTIBUY_THRESHOLD = 3;
export const NIBBLES_POUCH_MULTIBUY_DISCOUNT_PERCENTAGE = 0.1;
